/**
 * QueryBuilder Store - DATA(Q) script emission.
 *
 * Turns the structured state (Builders + TransformPipelines) into the actual
 * Data(Q) text the engine consumes. Two entry points:
 *   - `buildTransformPrefix`  → helper fields + parent QTABLEs + steps
 *   - `buildFilterPrefix`     → compound-condition FFIELD wiring for a builder
 *
 * Also exports drag-event MIME types and `isStepUsable` (consumed by the UI
 * validator too).
 *
 * Pure functions — no React, no store. Everything depends on the immutable
 * step tree passed in.
 */
import {
  BuilderTab,
  TransformPipeline,
  TransformStep,
} from "types/queryBuilder.types"
import ScriptGenerationService, {
  ScriptConfig,
} from "services/ScriptGenerationService"
import { walkAllSteps } from "./helpers"

/** Quick check used by both `useBuilderScript` and the batch emitter. Kept
 *  here so `buildBatchScript` doesn't have to depend on `selectors.ts`. */
const builderIsRunnable = (
  b: Pick<BuilderTab, "rowVariables" | "columnVariables">
): boolean => b.rowVariables.length > 0 && b.columnVariables.length > 0

export const QC_BUILDER_DRAG_TYPE = "application/x-dataq-variable"
export const QC_BUILDER_DRAG_LEAVES_TYPE =
  "application/x-dataq-variable-leaves"
/** MIME type used when dragging a transform step to reorder it. */
export const QC_STEP_REORDER_TYPE = "application/x-dataq-step-reorder"

export const buildFilterPrefix = (
  builder: BuilderTab
): {
  prefix: string
  filterFieldName: string | null
  usedSimpleUniverse: boolean
} => {
  const groups = builder.filterGroups ?? []
  const populated = groups.filter((g) => g.variables.length > 0)
  if (populated.length === 0) {
    return { prefix: "", filterFieldName: null, usedSimpleUniverse: false }
  }
  const safeName = sanitizeIdent(builder.name)
  const containerName = `__qcb_filter_${safeName}_${builder.id
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .substring(0, 8)}`
  const fieldName = `${containerName}_field`
  const lines: string[] = [`QTABLE ${containerName}`]
  populated.forEach((group, idx) => {
    const subName = `${containerName}::g${idx + 1}`
    lines.push(`QTABLE ${subName}`)
    group.variables.forEach((v) => {
      lines.push(`APPEND ${subName} <<${v}>>`)
    })
  })
  let usedSimpleUniverse = false
  if (builder.universeFilter) {
    const subName = `${containerName}::g${populated.length + 1}`
    lines.push(`QTABLE ${subName}`)
    lines.push(`APPEND ${subName} <<${builder.universeFilter}>>`)
    usedSimpleUniverse = true
  }
  lines.push(`FFIELD ${fieldName} ${containerName}`)
  return {
    prefix: lines.join("\n"),
    filterFieldName: fieldName,
    usedSimpleUniverse,
  }
}

export const sanitizeIdent = (s: string): string =>
  s.replace(/[^a-zA-Z0-9_]/g, "_").substring(0, 24) || "builder"

/**
 * The one mapping from a Builder tab to the script-generation config, shared
 * by `useBuilderScript`, `buildBatchScript` and the backend save
 * (`useTransformBackend`), so a new builder field only has to be wired here.
 *
 * `filter` is the UNIVERSE to use: callers that emit the compound-filter
 * prefix pass its FFIELD name; when omitted, the builder's simple
 * `universeFilter` is used.
 */
export const builderToScriptConfig = (
  builder: BuilderTab,
  filter: string | undefined = builder.universeFilter || undefined
): ScriptConfig => ({
  tableId: sanitizeIdent(builder.name),
  // The user-controlled engine table id. Stable across runs, validated
  // unique by the QueryBuilder UI.
  explicitOutputId: builder.outputName,
  rowVariables: builder.rowVariables,
  columnVariables: builder.columnVariables,
  selectedOperators: builder.selectedOperators,
  tableOperationType: builder.operation,
  filter,
  significance: builder.significance,
})

/**
 * Whether a step has the minimum data to emit anything useful.
 */
export const isStepUsable = (s: TransformStep): boolean => {
  switch (s.type) {
    case "combine_or":
    case "and_combine":
      // Both modes need a target name and at least one source. In
      // "into_existing" the name is the existing field; in "create" it's
      // the new field about to be declared.
      return !!s.name.trim() && s.sources.length > 0
    case "negate":
      // in_place needs only the source; create needs both name + source.
      return s.mode === "in_place"
        ? !!s.source.trim()
        : !!s.name.trim() && !!s.source.trim()
    case "cleanup":
      return !!s.source.trim()
    case "qtable":
      return !!s.name.trim()
    case "field_decl":
      // Name must be non-empty; initial value is optional (engine treats
      // an absent value as null). Type is always set via the editor.
      return !!s.name.trim()
    case "append":
      return !!s.parent.trim() && s.children.length > 0
    case "remove":
      return s.targets.length > 0
    case "conditional_assign":
      return (
        !!s.target.trim() &&
        (s.valueMode !== "field" || !!s.valueField.trim())
      )
    case "copy_structure":
      return !!s.target.trim() && !!s.source.trim()
    case "loop":
      // A loop is only usable if its iteration is configured AND the body
      // contains at least one usable step — an empty loop emits dead FOR
      // header + ENDFOR with nothing in between.
      if (!s.variable.trim()) return false
      if (s.iteration.kind === "field") {
        if (!s.iteration.source.trim()) return false
      } else if (s.iteration.values.length === 0) {
        return false
      }
      return s.body.some(isStepUsable)
    case "define":
      // The engine accepts an empty value (the symbol gets defined to nothing).
      // We only require a syntactically-legal symbol.
      return /^[A-Za-z_]\w*$/.test(s.symbol.trim())
    case "appendstruct":
      return !!s.target.trim() && !!s.source.trim()
    case "calc":
      return !!s.target.trim() && !!s.expression.trim()
    case "cat":
      // Needs a container, a source field, and at least one complete category
      // (both a label and a range). Half-filled rows are dropped at emission.
      return (
        !!s.target.trim() &&
        !!s.source.trim() &&
        s.categories.some((c) => c.label.trim() && c.range.trim())
      )
    case "weighting":
      if (s.mode === "apply") return !!s.weightsField.trim()
      // calculate: needs a destination field and at least one dimension with a
      // container and at least one complete (category, target%) goal.
      return (
        !!s.destField.trim() &&
        s.dimensions.some(
          (d) =>
            d.container.trim() &&
            d.targets.some((t) => t.label.trim() && t.target.trim())
        )
      )
  }
}

/**
 * Names of new variables / containers the step introduces. Used to seed
 * parent QTABLE declarations for nested paths.
 */
const stepIntroducesName = (s: TransformStep): string | null => {
  switch (s.type) {
    case "combine_or":
    case "and_combine":
      // "into_existing" doesn't create a new path, so don't pre-declare
      // QTABLE parents for it.
      return s.mode === "create" ? s.name.trim() || null : null
    case "negate":
      return s.mode === "create" ? s.name.trim() || null : null
    case "qtable":
      return s.name.trim() || null
    case "field_decl":
      // Same as qtable — `Parent//child` paths get a parent QTABLE pre-
      // declared via `collectParentPaths`. Bare names skip that step.
      return s.name.trim() || null
    case "append":
      return s.parent.trim() || null
    case "conditional_assign":
    case "copy_structure":
      return s.target.trim() || null
    case "calc":
      // "into_existing" doesn't create a new path; "create" declares the field.
      return s.mode === "create" ? s.target.trim() || null : null
    case "weighting":
      // calculate mode declares a REAL weights field; apply mode declares
      // nothing.
      return s.mode === "calculate" ? s.destField.trim() || null : null
    case "cleanup":
    case "remove":
    case "loop":
    case "define":
    case "appendstruct":
    case "cat":
      // Loops emit FOR/ENDFOR; DEFINE is a preprocessor directive; APPEND-
      // STRUCT and CAT require their target container to already exist, so
      // they don't pre-declare a fresh tree path (CAT emits its BOOL children
      // inline). Body steps inside loops are walked separately by
      // collectParentPaths via walkAllSteps.
      return null
  }
}

/**
 * Collect all parent paths that need a QTABLE container, derived from the
 * names of new variables the step pipeline introduces. Walks into loop
 * bodies so nested `qtable`/`append` steps still get their parents
 * pre-declared.
 *
 * Caveat: paths produced by loop-variable substitution at runtime (e.g.
 * `TITLE//BANK//value` becomes `TITLE//Bank 1//value`, etc.) are seen here
 * with the literal substring `BANK` — we may pre-emit a harmless
 * `QTABLE <<TITLE//BANK>>` that's never referenced. Acceptable for v1.
 */
const collectParentPaths = (steps: TransformStep[]): string[] => {
  const seen = new Set<string>()
  const paths: string[] = []
  for (const s of walkAllSteps(steps)) {
    if (!isStepUsable(s)) continue
    const name = stepIntroducesName(s)
    if (!name) continue
    const parts = name.split("//")
    for (let i = 1; i < parts.length; i++) {
      const parent = parts.slice(0, i).join("//")
      if (!seen.has(parent)) {
        seen.add(parent)
        paths.push(parent)
      }
    }
  }
  paths.sort((a, b) => a.split("//").length - b.split("//").length)
  return paths
}

/**
 * Whether the pipeline ever needs the `<<Boolean//True>>` helper field.
 * Auto-emitted as prologue if true. Walks into loop bodies so a nested
 * `conditional_assign` still triggers the prologue.
 */
const needsBooleanTrueHelper = (steps: TransformStep[]): boolean =>
  walkAllSteps(steps).some(
    (s) =>
      isStepUsable(s) &&
      s.type === "conditional_assign" &&
      s.valueMode === "true"
  )

/** Whether the pipeline needs the `Blank` helper field. Walks into bodies. */
const needsBlankHelper = (steps: TransformStep[]): boolean =>
  walkAllSteps(steps).some(
    (s) =>
      isStepUsable(s) &&
      s.type === "conditional_assign" &&
      s.valueMode === "blank"
  )

/** Cheap, unique-ish suffix for temp fields generated per-step. */
const stepTempSuffix = (id: string): string =>
  id.replace(/[^a-zA-Z0-9_]/g, "_").substring(0, 8) || "tmp"

/**
 * Generate Data(Q) for a single step.
 */
const generateStepScript = (s: TransformStep): string => {
  if (!isStepUsable(s)) return ""
  switch (s.type) {
    case "combine_or": {
      const target = s.name.trim()
      const lines: string[] = []
      if (s.mode === "create") {
        lines.push(`FIELD <<${target}>> BOOL false`)
      }
      s.sources.forEach((src) =>
        lines.push(`OR <<${target}>> <<${src}>>`)
      )
      return lines.join("\n")
    }
    case "and_combine": {
      const target = s.name.trim()
      const lines: string[] = []
      if (s.mode === "create") {
        // First source seeds the field; subsequent sources AND in.
        const [first, ...rest] = s.sources
        lines.push(`FIELD <<${target}>> BOOL`)
        lines.push(`ASSIGN <<${target}>> <<${first}>>`)
        rest.forEach((src) => lines.push(`AND <<${target}>> <<${src}>>`))
      } else {
        // into_existing: AND every source into the existing field.
        s.sources.forEach((src) =>
          lines.push(`AND <<${target}>> <<${src}>>`)
        )
      }
      return lines.join("\n")
    }
    case "negate": {
      if (s.mode === "in_place") {
        return `NOT <<${s.source.trim()}>>`
      }
      const name = s.name.trim()
      return [
        `FIELD <<${name}>> BOOL`,
        `ASSIGN <<${name}>> <<${s.source}>>`,
        `NOT <<${name}>>`,
      ].join("\n")
    }
    case "cleanup": {
      const cmd =
        s.mode === "true"
          ? "BLANKTRUE"
          : s.mode === "zero"
          ? "BLANKZERO"
          : "BLANKFALSE"
      return `${cmd} <<${s.source}>>`
    }
    case "qtable":
      return `QTABLE <<${s.name.trim()}>>`
    case "field_decl": {
      // `FIELD <<Name>> TYPE [init]`. The engine accepts an omitted init
      // (field starts null) so the trailing token is conditional. TEXT
      // values containing whitespace get wrapped in double quotes so the
      // tokenizer sees them as one argument — for the other types the
      // value is a single token already.
      const name = s.name.trim()
      const raw = s.initialValue.trim()
      let initial = ""
      if (raw) {
        if (s.fieldType === "TEXT" && /\s|"/.test(raw)) {
          initial = ` "${raw.replace(/"/g, '\\"')}"`
        } else {
          initial = ` ${raw}`
        }
      }
      return `FIELD <<${name}>> ${s.fieldType}${initial}`
    }
    case "append": {
      const parent = s.parent.trim()
      // Ensure parent exists before appending.
      const lines: string[] = [`QTABLE <<${parent}>>`]
      s.children.forEach((c) => lines.push(`APPEND <<${parent}>> <<${c}>>`))
      return lines.join("\n")
    }
    case "remove":
      return s.targets.map((t) => `REMOVE <<${t}>>`).join("\n")
    case "conditional_assign": {
      const target = s.target.trim()
      const valueRef =
        s.valueMode === "true"
          ? `<<Boolean//True>>`
          : s.valueMode === "blank"
          ? `Blank`
          : `<<${s.valueField.trim()}>>`
      const lines: string[] = [`FIELD <<${target}>> BOOL`]
      if (s.conditions.length === 0) {
        lines.push(`ASSIGN <<${target}>> ${valueRef}`)
      } else if (s.conditions.length === 1) {
        lines.push(`ASSIGN <<${target}>> ${valueRef} IF:<<${s.conditions[0]}>>`)
      } else {
        // Multiple conditions: OR them into a temp, then ASSIGN IF:Temp.
        const temp = `__qcb_cond_${stepTempSuffix(s.id)}`
        lines.push(`FIELD ${temp} BOOL false`)
        s.conditions.forEach((c) => lines.push(`OR ${temp} <<${c}>>`))
        lines.push(`ASSIGN <<${target}>> ${valueRef} IF:${temp}`)
        lines.push(`REMOVE ${temp}`)
      }
      return lines.join("\n")
    }
    case "copy_structure":
      return [
        `QTABLE <<${s.target.trim()}>>`,
        `COPY <<${s.target.trim()}>> <<${s.source.trim()}>>`,
      ].join("\n")
    case "loop": {
      const v = s.variable.trim()
      let header: string
      if (s.iteration.kind === "field") {
        // The exclude marker has to be a *standalone* `-` token (followed by
        // the values to skip) for the engine's `TokenIsExclude` check to
        // fire — the engine tokenizer only splits on whitespace, so values
        // containing whitespace ("None of the above") need to be quoted to
        // stay one token.
        // `?? []` guards against unmigrated state slipping through (e.g.
        // a sidecar loaded before `normalizeHydratedSteps` ran).
        const excluded = (s.iteration.excludedValues ?? [])
          .map((val) => val.trim())
          .filter(Boolean)
        let excludeSuffix = ""
        if (excluded.length > 0) {
          const tokens = excluded.map((val) =>
            /\s/.test(val) || /["]/.test(val)
              ? `"${val.replace(/"/g, '\\"')}"`
              : val
          )
          excludeSuffix = " - " + tokens.join(" ")
        }
        header = `FOR ${v} <<${s.iteration.source.trim()}>>${excludeSuffix}`
      } else {
        const quoted = s.iteration.values
          .map((val) => `"${val.replace(/"/g, '\\"')}"`)
          .join(" ")
        header = `FOR ${v} ${quoted}`
      }
      const bodyLines = s.body
        .map(generateStepScript)
        .filter((line) => line.length > 0)
      return [header, ...bodyLines, "ENDFOR"].join("\n")
    }
    case "define": {
      // No `<<…>>` wrapping — DEFINE's symbol is a bare identifier, and the
      // value is free text that may itself contain `<<…>>` references the
      // engine resolves on substitution.
      const sym = s.symbol.trim()
      const val = s.value.trim()
      return val ? `DEFINE ${sym} ${val}` : `DEFINE ${sym}`
    }
    case "appendstruct": {
      const target = s.target.trim()
      const source = s.source.trim()
      const alias = s.alias.trim()
      // No QTABLE prefix here, unlike the `append` step. APPENDSTRUCT's
      // canonical usage in real pipelines is "create the target once
      // outside a loop with a qtable step, then APPENDSTRUCT inside the
      // loop with a per-iteration alias to fan out children." An auto-
      // prepended QTABLE would re-create the constant target on every
      // iteration and throw on the second pass. So the user is responsible
      // for ensuring the target exists — `collectStepIssues` flags the
      // common mistake of forgetting to add a preceding qtable step.
      if (alias) {
        // The alias is the quoted third argument — escape any embedded
        // quotes so the engine sees one well-formed quoted token.
        const safe = alias.replace(/"/g, '\\"')
        return `APPENDSTRUCT <<${target}>> <<${source}>> "${safe}"`
      }
      return `APPENDSTRUCT <<${target}>> <<${source}>>`
    }
    case "calc": {
      const target = s.target.trim()
      // The whole expression is the engine's quoted second argument. Escape
      // any embedded double quotes so the tokenizer sees one token (CALC
      // expressions normally contain none — they're <<refs>>, numbers and
      // operators — but escape defensively).
      const expr = s.expression.trim().replace(/"/g, '\\"')
      const calcLine = `CALC <<${target}>> "${expr}"`
      return s.mode === "create"
        ? `FIELD <<${target}>> ${s.fieldType}\n${calcLine}`
        : calcLine
    }
    case "cat": {
      const target = s.target.trim()
      const source = s.source.trim()
      // The category labels ARE the target container's existing BOOL children
      // (auto-populated in the editor), so CAT only emits the mapping line —
      // it does not declare the fields or the container. A label without a
      // range can't be mapped, so it's skipped (the validator warns about it,
      // since the engine needs every BOOL child covered).
      const spec = s.categories
        .filter((c) => c.label.trim() && c.range.trim())
        .map((c) => {
          const label = c.label.trim()
          // The engine allows wrapping a label in <<…>> when it contains the
          // spec's own delimiters (':' / ';'); plain labels stay bare.
          const labelToken = /[;:]/.test(label) ? `<<${label}>>` : label
          return `${labelToken}: ${c.range.trim()}`
        })
        .join("; ")
      return `CAT <<${target}>> <<${source}>> "${spec}"`
    }
    case "weighting": {
      if (s.mode === "apply") {
        return `USEWEIGHTS <<${s.weightsField.trim()}>>`
      }
      // calculate (rim/rake weighting):
      //   FIELD <<dest>> REAL         ← blank: CALCWEIGHTS only fills blanks
      //   CALCWEIGHTS <<dest>> <<cat>> tgt … [IF <<filter>>] AVERAGE|SUM v
      //   USEWEIGHTS <<dest>>         ← apply the computed weights
      // Goals from every dimension are folded into ONE CALCWEIGHTS so the
      // engine rakes all the marginals jointly (IPF). The engine requires a
      // scale, so "none" (or a blank value) emits AVERAGE 1.
      const dest = s.destField.trim()
      const goals = s.dimensions
        .flatMap((d) => {
          const container = d.container.trim()
          if (!container) return []
          return d.targets
            .filter((t) => t.label.trim() && t.target.trim())
            .map(
              (t) => `<<${container}//${t.label.trim()}>> ${t.target.trim()}`
            )
        })
        .join(" ")
      let calcLine = `CALCWEIGHTS <<${dest}>> ${goals}`
      if (s.filter.trim()) calcLine += ` IF <<${s.filter.trim()}>>`
      const scaleValue = s.normalizeValue.trim()
      if (s.normalize === "sum" && scaleValue) {
        calcLine += ` SUM ${scaleValue}`
      } else if (s.normalize === "average" && scaleValue) {
        calcLine += ` AVERAGE ${scaleValue}`
      } else {
        calcLine += ` AVERAGE 1`
      }
      return [
        `FIELD <<${dest}>> REAL`,
        calcLine,
        `USEWEIGHTS <<${dest}>>`,
      ].join("\n")
    }
  }
}

/**
 * Build the transformation prefix: helper fields (Boolean::True, Blank) when
 * needed, then QTABLE for nested-path parents, then each step in declared
 * order. Names are wrapped in <<…>> so spaces and slashes are safe.
 */
export const buildTransformPrefix = (steps: TransformStep[]): string => {
  const usable = steps.filter(isStepUsable)
  if (usable.length === 0) return ""
  const lines: string[] = []
  if (needsBooleanTrueHelper(usable)) {
    lines.push(`FIELD <<Boolean//True>> BOOL true`)
  }
  if (needsBlankHelper(usable)) {
    lines.push(`FIELD Blank BOOL`)
  }
  collectParentPaths(usable).forEach((p) => {
    lines.push(`QTABLE <<${p}>>`)
  })
  usable.forEach((s) => {
    const script = generateStepScript(s)
    if (script) lines.push(script)
  })
  return lines.join("\n")
}

const stripLeadingReset = (s: string): string =>
  s.startsWith("RESET\n") ? s.slice("RESET\n".length) : s

/**
 * Build a single DATA(Q) script that runs every runnable Builder in one
 * engine call:
 *
 *   - One leading `RESET` (per-builder RESETs from `generateComplexScript`
 *     are stripped before concatenation).
 *   - The set of distinct `transformPipelineId`s referenced by any runnable
 *     builder is emitted **once each**, in encounter order — so two
 *     builders sharing Pipeline A don't re-run the prologue twice.
 *   - Helper fields (`<<Boolean//True>>`, `Blank`) and parent QTABLEs are
 *     computed across the union of those pipelines' steps and emitted at
 *     most once each, eliminating "FIELD already declared" engine errors
 *     when two pipelines both need the same helper.
 *   - Per builder: filter prefix (FFIELD names already include the builder
 *     id → no collisions), then the builder's QTABLE/XTAB/OUTPUT block.
 *
 * Returns the assembled script plus the ordered list of builder ids that
 * actually contributed an XTAB/DTAB. The caller maps `result.output[]` →
 * `includedBuilderIds[i]` by index (engine preserves OUTPUT order).
 */
export const buildBatchScript = (
  builders: BuilderTab[],
  pipelines: TransformPipeline[]
): { script: string; includedBuilderIds: string[] } => {
  const runnable = builders.filter(builderIsRunnable)
  if (runnable.length === 0) {
    return { script: "", includedBuilderIds: [] }
  }

  const pipelineMap = new Map(pipelines.map((p) => [p.id, p]))
  const usedPipelineIds: string[] = []
  for (const b of runnable) {
    const pid = b.transformPipelineId
    if (pid && pipelineMap.has(pid) && !usedPipelineIds.includes(pid)) {
      usedPipelineIds.push(pid)
    }
  }
  const usedPipelines: TransformPipeline[] = usedPipelineIds
    .map((id) => pipelineMap.get(id))
    .filter((p): p is TransformPipeline => !!p)

  const lines: string[] = ["RESET"]

  // Shared transform prologue. We treat the union of all included
  // pipelines' steps as the input for helper-field / parent-QTABLE
  // collection — that way each helper or parent is emitted exactly once
  // across the batch, even when pipelines overlap on what they need.
  const allUsableSteps = usedPipelines.flatMap((p) =>
    p.steps.filter(isStepUsable)
  )
  if (allUsableSteps.length > 0) {
    if (needsBooleanTrueHelper(allUsableSteps)) {
      lines.push(`FIELD <<Boolean//True>> BOOL true`)
    }
    if (needsBlankHelper(allUsableSteps)) {
      lines.push(`FIELD Blank BOOL`)
    }
    collectParentPaths(allUsableSteps).forEach((p) => {
      lines.push(`QTABLE <<${p}>>`)
    })
    // Emit each pipeline's step bodies in encounter order. The helper +
    // parents block above is shared, so we don't re-run `buildTransform-
    // Prefix` per pipeline (that would duplicate them).
    for (const p of usedPipelines) {
      p.steps.filter(isStepUsable).forEach((s) => {
        const out = generateStepScript(s)
        if (out) lines.push(out)
      })
    }
  }

  // Per-builder filter + table block. `generateComplexScript` produces a
  // self-contained script with its own leading `RESET\n` — we strip it
  // before concatenation so the batch keeps to a single RESET at the top.
  const includedBuilderIds: string[] = []
  for (const b of runnable) {
    const filterInfo = buildFilterPrefix(b)
    if (filterInfo.prefix) lines.push(filterInfo.prefix)
    const xtabFilter = filterInfo.filterFieldName
      ? filterInfo.filterFieldName
      : b.universeFilter || undefined
    let block = ScriptGenerationService.generateComplexScript(
      builderToScriptConfig(b, xtabFilter)
    )
    block = stripLeadingReset(block)
    if (b.isTransposed) {
      block = block.replace(/^OUTPUT (\S+)\s*$/m, "OUTPUT $1 /T")
    }
    lines.push(block)
    includedBuilderIds.push(b.id)
  }

  return { script: lines.join("\n"), includedBuilderIds }
}
